import { Check, Minus, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionHead } from "@/components/Reveal";
import { APP_LINKS, COMPARISON_ROWS, PLANS } from "@/data/content";

const THEMES = {
  gold: "border-gold bg-gradient-to-b from-gold/10 via-card to-card shadow-[0_20px_70px_rgba(212,175,55,0.18)]",
  "gold-soft": "border-border bg-card hover:border-gold/40",
  green: "border-mbcg/60 bg-gradient-to-b from-mbcg/15 via-card to-card shadow-[0_20px_70px_rgba(16,185,129,0.18)]",
};

function PlanCard({ plan, i }) {
  const isGreen = plan.theme === "green";
  return (
    <Reveal delay={i * 0.1} className="h-full">
      <div
        className={`relative flex h-full flex-col rounded-3xl border p-8 transition-transform duration-300 hover:-translate-y-2 ${THEMES[plan.theme]}`}
        data-testid={`pricing-card-${plan.id}`}
      >
        {plan.badge && (
          <span
            className={`absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] ${
              isGreen ? "bg-mbcg text-night" : "bg-gold text-night"
            }`}
            data-testid={`pricing-badge-${plan.id}`}
          >
            {plan.badge}
          </span>
        )}
        <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-foreground">{plan.name}</h3>
        <p className="mt-2 min-h-[40px] text-sm text-muted-foreground">{plan.desc}</p>
        <div className="mt-6 flex items-baseline gap-1.5">
          <span className={`font-heading text-4xl font-extrabold tracking-tight sm:text-5xl ${isGreen ? "text-mbcg" : "text-gold-grad"}`}>
            {plan.price}
          </span>
          <span className="text-sm text-muted-foreground">{plan.period}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground italic">{plan.note}</p>
        <ul className="mt-7 flex-1 space-y-3">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/90">
              <span className={`mt-0.5 inline-flex h-4.5 w-4.5 h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ${isGreen ? "bg-mbcg/15 text-mbcg" : "bg-gold/15 text-gold"}`}>
                <Check className="h-3 w-3" />
              </span>
              {f}
            </li>
          ))}
        </ul>
        {isGreen ? (
          <Link
            to="/contact?sujet=Devenir%20partenaire"
            data-testid={`pricing-cta-${plan.id}`}
            className="btn-sheen mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-mbcg px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-night transition-all duration-300 hover:bg-mbcg-hover"
          >
            Devenir partenaire <ArrowRight className="h-4 w-4" />
          </Link>
        ) : (
          <a
            href={APP_LINKS.joinClub}
            data-testid={`pricing-cta-${plan.id}`}
            className="btn-sheen mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold-deep via-gold to-gold-hover px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-night transition-transform duration-300 hover:scale-[1.03]"
          >
            Devenir membre <ArrowRight className="h-4 w-4" />
          </a>
        )}
      </div>
    </Reveal>
  );
}

function Cell({ value, green }) {
  if (value === true) return <Check className={`mx-auto h-4 w-4 ${green ? "text-mbcg" : "text-gold"}`} data-testid={green ? undefined : "comparison-check"} />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" />;
  return <span className={`text-xs font-semibold ${green ? "text-mbcg" : "text-foreground/85"}`}>{value}</span>;
}

function ComparisonTable() {
  return (
    <Reveal delay={0.1}>
      <div className="mt-20" data-testid="pricing-comparison">
        <div className="mb-8 flex items-center gap-3">
          <span className="h-px w-10 bg-gold/70" />
          <span className="eyebrow">Comparatif des formules</span>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-border" data-testid="comparison-table-wrapper">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead>
              <tr className="border-b border-border bg-night-soft/80">
                <th className="px-6 py-5 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Avantages</th>
                {PLANS.map((p) => (
                  <th
                    key={p.id}
                    className={`px-6 py-5 text-center font-heading text-sm font-bold uppercase tracking-wide ${
                      p.theme === "green" ? "bg-mbcg/10 text-mbcg" : p.highlight ? "text-gold" : "text-foreground"
                    }`}
                  >
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, ri) => (
                <tr key={row.label} className={`border-b border-border/60 ${ri % 2 === 1 ? "bg-card/60" : ""}`}>
                  <td className="px-6 py-4 text-sm font-medium text-foreground/90">{row.label}</td>
                  <td className="px-6 py-4 text-center"><Cell value={row.pro} /></td>
                  <td className="px-6 py-4 text-center"><Cell value={row.premium} /></td>
                  <td className="bg-mbcg/5 px-6 py-4 text-center"><Cell value={row.partenaire} green /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Reveal>
  );
}

export default function Pricing() {
  return (
    <section id="adhesions" className="relative overflow-hidden py-16 md:py-24 lg:py-32" data-testid="pricing-section">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)" }}
        aria-hidden="true"
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Adhésions"
          title="Choisissez votre formule."
          sub="Trois façons de rejoindre le club, selon votre profil et vos ambitions."
          align="center"
        />
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-6" data-testid="pricing-grid">
          {PLANS.map((p, i) => (
            <PlanCard key={p.id} plan={p} i={i} />
          ))}
        </div>
        <ComparisonTable />
      </div>
    </section>
  );
}
